import { useCallback, useEffect, useMemo } from "react";
import { debouncePromise } from "src/utils/debounce-promise";
import { useAppDispatch, useCoreStore } from "./redux";
import {
  calculateAddonSalesTax,
  calculateRecommendedPlanSalesTax,
} from "src/utils/api/purchase/checkout";
import { selectAllPaymentAddons } from "src/utils/redux/slices/paymentAddon";
import { isShowablePaymentAddon } from "src/utils/paymentAddon";
import { isEligibleForOrdersMedCheckout } from "src/utils/userSegment/features";
import { useOrdersCheckoutMethods } from "./checkout/ordersCheckout";

type CalculateNowFn = () => Promise<void>;

const latestCalculateNowRef: { current?: CalculateNowFn } = {};

const debouncedCalculateSalesTax = debouncePromise(
  async () => {
    if (latestCalculateNowRef.current) {
      return latestCalculateNowRef.current();
    }
    return Promise.resolve();
  },
  { timeout: 300 }
);

export function useSalesTax() {
  const dispatch = useAppDispatch();
  const store = useCoreStore();
  const { calculateSalesTaxForOrder } = useOrdersCheckoutMethods();

  const calculateNow = useCallback(async () => {
    if (isEligibleForOrdersMedCheckout()) {
      await calculateSalesTaxForOrder();
      return;
    }

    const state = store.getState();
    const paymentAddons = selectAllPaymentAddons(state.paymentAddon).filter(
      isShowablePaymentAddon
    );

    const promises: Array<Promise<any>> = [];
    if (state.recommendedPlan.noom_plan_id) {
      promises.push(dispatch(calculateRecommendedPlanSalesTax()));
    }
    if (paymentAddons.length) {
      promises.push(dispatch(calculateAddonSalesTax()));
    }
    await Promise.all(promises);
  }, [dispatch, store, calculateSalesTaxForOrder]);

  useEffect(() => {
    latestCalculateNowRef.current = calculateNow;
    return () => {
      if (latestCalculateNowRef.current === calculateNow) {
        latestCalculateNowRef.current = undefined;
      }
    };
  }, [calculateNow]);

  const calculateSalesTaxDebounced = useMemo(() => {
    return async () => {
      await debouncedCalculateSalesTax();
    };
  }, []);

  return {
    calculateSalesTaxDebounced,
  };
}
